/**
 * E5. Ставки на спорт: раз в год чемпионат из восьми вымышленных команд.
 * Силы команд — логнормальный жребий `Rng`, честные вероятности p_i = s_i/Σs,
 * коэффициенты с маржой 6 %: odds_i = 1 / (p_i · 1,06), так что Σ 1/odds = 1,06.
 * В «restricted» странах — через знакомого букмекера с комиссией 15 % от выплаты.
 *
 * Исход ставки известен в конце года: `play` снимает ставку и ставит
 * флаги `betPending*`, движок разрешает их на следующем тике (`resolveBet`).
 * В журнал кладётся ключ названия команды (`nameKey`); `t()` переводит
 * параметр-ключ при отрисовке.
 * С 18 лет, legal и restricted. Тяга +3.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { gamblingLaw } from '@life/data';
import {
  FLAG_BET_AMOUNT,
  FLAG_BET_ODDS,
  FLAG_BET_P,
  FLAG_BET_PENDING,
  FLAG_BET_TEAM,
  type Life,
  type MinigameOutcome,
  type Rng,
} from '@life/engine';
import type { MinigameDef } from '../types';
import { CASINO_MIN_AGE, clampBet, expectancy, noPlay } from './common';

export const BETS_TEAMS = 8;
export const BETS_MARGIN = 0.06;
export const BETS_RESTRICTED_COMMISSION = 0.15;
export const BETS_URGE = 3;
/** Разброс силы команд (sigma логнормального). */
export const BETS_STRENGTH_SIGMA = 0.5;

export interface BetsTeam {
  id: number;
  nameKey: string;
  /** Честная вероятность победы в чемпионате. */
  p: number;
  /** Коэффициент полного возврата (с маржой, без комиссии). */
  odds: number;
}

export interface BetsState {
  teams: BetsTeam[];
  maxBet: number;
  /** Доля выплаты, удерживаемая букмекером (restricted → 0,15). */
  commission: number;
}

export interface BetsInput {
  team: number;
  bet: number;
}

export function betsRtp(commission: number): number {
  return (1 - commission) / (1 + BETS_MARGIN);
}

export function makeTeams(rng: Rng): BetsTeam[] {
  const strengths: number[] = [];
  for (let i = 0; i < BETS_TEAMS; i += 1) strengths.push(rng.logNormal(0, BETS_STRENGTH_SIGMA));
  const total = strengths.reduce((s, x) => s + x, 0);
  return strengths.map((s, i) => {
    const p = s / total;
    return { id: i, nameKey: `minigame.bets.team.${i + 1}`, p, odds: 1 / (p * (1 + BETS_MARGIN)) };
  });
}

export const bets: MinigameDef<BetsInput, BetsState> = {
  id: 'bets',
  section: 'E',
  triggers: [{ kind: 'age', min: CASINO_MIN_AGE, max: 120, everyYears: 1 }],
  available(life: Life, _ds: Dataset, law: GamblingLaw): boolean {
    return life.age >= CASINO_MIN_AGE && law !== 'illegal';
  },
  setup(life: Life, _ds: Dataset, rng: Rng): BetsState {
    const law = gamblingLaw(life.iso3).law;
    return { teams: makeTeams(rng), maxBet: Math.max(0, life.money), commission: law === 'restricted' ? BETS_RESTRICTED_COMMISSION : 0 };
  },
  play(state: BetsState, input: BetsInput, life: Life): MinigameOutcome {
    const team = state.teams[Math.max(0, Math.min(state.teams.length - 1, Math.trunc(input.team) || 0))] as BetsTeam;
    const bet = clampBet(Math.min(input.bet, state.maxBet), life.money);
    if (bet <= 0) return noPlay();
    const payoutOdds = team.odds * (1 - state.commission);
    return {
      moneyDelta: -bet,
      urgeDelta: BETS_URGE,
      flags: {
        [FLAG_BET_PENDING]: true,
        [FLAG_BET_TEAM]: team.id,
        [FLAG_BET_AMOUNT]: bet,
        [FLAG_BET_ODDS]: payoutOdds,
        [FLAG_BET_P]: team.p,
      },
      logKey: 'minigame.bets.placed',
      logParams: { amount: Math.round(bet), team: team.nameKey, odds: Math.round(payoutOdds * 100) / 100 },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: (state: BetsState) => expectancy('bets', betsRtp(state.commission)),
};
